<svelte:head>
  <title>Businesses | A Good Thing</title>
</svelte:head>

<nav class="level">
  <!-- Left side -->
  <div class="level-left">
    <div class="level-item">
      <h2 class="title">Businesses</h2>
    </div>
  </div>

  <!-- Right side -->
  <div class="level-right">
    <SearchInput bind:value={searchTerm}/>
    &nbsp;
    <button class="button" on:click={() => { showFilterPanel = !showFilterPanel}}>Filter by location</button>
    {#if $appState.user?.isFullAdmin}
    &nbsp;
    <a class="button is-primary" href={$url("/admin/business/create")}>New</a>
    {/if}
  </div>
</nav>


{#if showFilterPanel}

<div class="block">
  <p class="subtitle">Filter by location</p>
  <div class="field is-horizontal">
    <div class="field-label is-normal">
      <label for="firstName" class="label">Postcode</label>
    </div>
    <div class="field-body">
      <div class="field">
        <div class="control">
          <PostcodeInput bind:postcode={filters.geo.postcode}/>
        </div>
      </div>
    </div>
  </div>
  <div class="field is-horizontal">
    <div class="field-label is-normal">
      <label for="firstName" class="label">Distance</label>
    </div>
    <div class="field-body">
      <div class="field">
        <div class="control">
          <div class="select">
            <select bind:value={filters.geo.distance}>
              <option value={1}>1</option>
              <option value={3}>3</option>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="field is-horizontal">
    <div class="field-label">
      <!-- Left empty for spacing -->
    </div>
    <div class="field-body">
      <div class="field">
        <div class="control">
          <button
            class:is-loading={isLoading}
            class="button is-primary"
            on:click={() => getGeoFiltered()}>Filter</button
          >
          <button
            class="button"
            on:click={() => { filters.geo.postcode = ''; getAll(); }}>Clear</button
          >
        </div>
      </div>
    </div>
  </div>

</div>

{/if}

<div class="level">
  <div class="level-left">
    <div class="level-item">
      <p class="has-text-grey-light is-size-7">
        {count} records ({uniqueCount} unique by name)
      </p>
    </div>
  </div>
</div>

<table class="table is-fullwidth is-hoverable">
  <thead>
    <tr>
      <th class="has-text-left">Name <SortIcon field='data.name' {sortField} {sortAsc} on:toggle={(ev) => toggleSort(ev.detail.field)}/></th>
      <th class="has-text-left">Created <SortIcon field='data.created' {sortField} {sortAsc} on:toggle={(ev) => toggleSort(ev.detail.field)}/></th>
      <th />
    </tr>
  </thead>
  <tbody>
    {#each items as item}
      <tr>
        <td>
          <div
            class="is-flex is-flex-direction-row align-items is-align-items-center"
          >
            <a href={$url("../" + item.id)}>{item.data.name} {#if item.data.deactivated} (deactivated){/if}</a>
          </div>
        </td>
        <td>
          <div
            class="is-flex is-flex-direction-row align-items is-align-items-center"
          >
            {dayjs(item.data.created).format('YYYY/MM/DD HH:mm')}
          </div>
        </td>
        <td>
          <div
            class="is-flex is-flex-direction-row align-items is-align-items-center"
          />
        </td>
      </tr>
    {/each}
  </tbody>
</table>

<script lang="ts">
  import _ from "lodash";
  import dayjs from "dayjs";
  //import localizedFormat from 'dayjs/plugin/localizedFormat';
  import { url, params } from "@sveltech/routify";
  import { geoQuery } from "../../../lib/geo";
  import { appState } from "../../../lib/appState";

  import PostcodeInput from "../../_components/PostcodeInput.svelte";
  import SearchInput from "../../_components/SearchInput.svelte";
  import SortIcon from "../../_components/SortIcon.svelte";

  //dayjs.extend(localizedFormat);

  let allItems: Company_v1_Doc[] = [];

  let searchTerm = '';
  let sortField = '';
  let sortAsc = true;

  let showFilterPanel = false;
  let isLoading = false;

  const filters = {
    geo: {
      postcode: '',
      distance: 20
    }
  }

  $: count = items.length;

  $: uniqueCount = _.uniqBy(items, "data.name").length;

  $: items = sort(sortField, sortAsc, filter(allItems, searchTerm));

  let firebase = window.firebase;

  if ($params.filter_postcode) {
    filters.geo.postcode = $params.filter_postcode;
    if($params.filter_distance) {
      filters.geo.distance = $params.filter_distance;
    }
    else {
      filters.geo.distance = 20;
    }

    showFilterPanel = true;
    getGeoFiltered();
  }
  else {
    getAll();
  }

  async function getAll() {

    let query;

    if($appState.user?.isFullAdmin) {
      query = firebase
      .firestore()
      .collection("companies_v1")
      .orderBy("name");
    }
    else {    
      query = firebase
      .firestore()
      .collection("companies_v1")
      .where("tags", "array-contains", $appState.user?.delegatedAdminTag);
    }  
      
    query.onSnapshot((snap) => {
        allItems = snap.docs.map(
          (doc) => ({ id: doc.id, data: doc.data() } as Company_v1_Doc)
        );
      });
  }

  async function getGeoFiltered() {
    if(filters.geo.postcode && filters.geo.distance) {
      isLoading = true;
      try { 

        const location = await getPostcode(filters.geo.postcode);

        await getGeoQuery(
          location.longitude,
          location.latitude,
          filters.geo.distance);
      } finally {
        isLoading = false;
      }
    }
  }

  async function getPostcode(postcode: string) {
    return fetch(`https://api.postcodes.io/postcodes/${postcode}`)
      .then((res: any) => {
        return res.json();
      })
      .then((payload: any) => {
        return payload.result;
      });
  }

  async function getGeoQuery(longitude: number, latitude: number, distance: number) {

    let items = await geoQuery(
      firebase
        .firestore()
        .collection("companies_v1"),
      'geo',
      longitude,
      latitude,
      distance
      ) as Company_v1_Doc[];

    if($appState.user?.delegatedAdminTag) {
      items = items.filter((company) => $appState.user?.delegatedAdminTag && company.data.tags?.includes($appState.user?.delegatedAdminTag));
    }

    allItems = items;
  }

  function filter(_allItems :Company_v1_Doc[] , _searchTerm : string) : Company_v1_Doc[] {
    if (!searchTerm) {
      return allItems;
    }

    return _.filter(_allItems, (item) => {
      return (
        (item.data.name && item.data.name.toLowerCase().includes(_searchTerm.toLowerCase())) ||
        (item.data.tags?.includes(_searchTerm))
        );
    }) as Company_v1_Doc[];
  }

  function sort(_sortField :string, _sortAsc :boolean, _items:Company_v1_Doc[]) {
    return _.orderBy(_items, _sortField, _sortAsc ? 'asc' : 'desc');
  }

  function toggleSort(field :string) {
    sortField = field;
    sortAsc = !sortAsc;
  }
</script>
